import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  ShoppingBag, 
  Heart, 
  Search, 
  X, 
  Menu, 
  ArrowRight,
  Sparkles
} from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useWishlist } from '../../context/WishlistContext';

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getCartCount } = useCart();
  const { wishlist } = useWishlist();

  const [isScrolled, setIsScrolled] = useState(false);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const cartCount = getCartCount();
  const wishlistCount = wishlist.length;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsSearchActive(false);
  }, [location.pathname, location.search]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/catalog?search=${encodeURIComponent(searchQuery.trim())}`);
      setIsSearchActive(false);
      setSearchQuery('');
    }
  };

  // Updated navigation links tailored for Vedhyas
  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Catalog', path: '/catalog' },
    { name: 'Heritage', path: '/heritage' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname + location.search === path;
  };

  return (
    <>
      {/* Top Announcements Banner */}
      <div className="bg-[#20050A] text-[#E8DFC8] text-[10px] sm:text-[11px] tracking-widest uppercase py-2 px-4 text-center border-b border-[#4A0E17] flex items-center justify-center space-x-2">
        <Sparkles className="w-3 h-3 text-[#E8DFC8] shrink-0" />
        <span className="truncate">Complimentary Insured Delivery Across India</span>
      </div>

      {/* Main Sticky Header */}
      <header 
        className={`sticky top-0 z-40 w-full transition-all duration-300 ${
          isScrolled 
            ? 'bg-[#FDFBF7]/95 backdrop-blur-md shadow-md border-b border-[#E8DFC8] h-16' 
            : 'bg-[#FDFBF7]/85 backdrop-blur-sm border-b border-[#E8DFC8]/50 h-16 sm:h-20'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center justify-between relative">
          
          {/* MOBILE FULL-WIDTH IN-NAVBAR SEARCH */}
          {isSearchActive ? (
            <div className="w-full flex items-center space-x-2 animate-fade-in py-2">
              <form onSubmit={handleSearchSubmit} className="relative flex-1 flex items-center">
                <Search className="absolute left-3.5 w-4 h-4 text-[#4A0E17]" />
                <input
                  type="text"
                  autoFocus
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search silk sarees, fine jewelry..."
                  className="w-full bg-[#F4EFE6] text-xs sm:text-sm text-[#4A0E17] placeholder-[#7A6B5D]/70 pl-10 pr-9 py-2 rounded-full border border-[#4A0E17] outline-none shadow-sm transition-all"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 p-0.5 text-[#7A6B5D] hover:text-[#4A0E17]"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </form>
              <button
                type="button"
                onClick={() => {
                  setIsSearchActive(false);
                  setSearchQuery('');
                }}
                className="text-xs font-serif font-bold text-[#4A0E17] hover:text-[#6B1320] px-2 py-1 transition-colors"
              >
                Cancel
              </button>
            </div>
          ) : (
            /* REGULAR HEADER LAYOUT */
            <>
              {/* Mobile Hamburger Toggle */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2 -ml-2 text-[#4A0E17] hover:text-[#6B1320] transition-colors"
                aria-label="Toggle Navigation Menu"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>

              {/* Brand Logo */}
              <Link to="/" className="flex items-center space-x-1 group">
                <span className="text-2xl sm:text-3xl font-serif font-bold tracking-tight text-[#4A0E17] group-hover:opacity-90 transition-opacity">
                  Vedhyas<span className="text-[#6B1320] font-sans">.</span>
                </span>
              </Link>

              {/* Desktop Center Links */}
              <div className="hidden md:flex flex-1 items-center justify-center px-8 max-w-2xl">
                <nav className="flex items-center space-x-8 text-xs font-serif font-bold uppercase tracking-widest text-[#7A6B5D]">
                  {navLinks.map((link) => {
                    const active = isActive(link.path);
                    return (
                      <Link
                        key={link.name}
                        to={link.path}
                        className={`relative py-1 transition-colors duration-200 hover:text-[#4A0E17] whitespace-nowrap ${
                          active ? 'text-[#4A0E17]' : ''
                        }`}
                      >
                        {link.name}
                        {active && (
                          <span className="absolute bottom-0 left-0 w-full h-[2px] bg-[#4A0E17] rounded-full" />
                        )}
                      </Link>
                    );
                  })}
                </nav>
              </div>

              {/* Action Icons Bar */}
              <div className="flex items-center space-x-1 sm:space-x-3">
                
                {/* Search Icon Button */}
                {/* <button
                  onClick={() => setIsSearchActive(true)}
                  className="p-2 sm:p-2.5 text-[#4A0E17] hover:text-[#6B1320] hover:bg-[#F4EFE6] rounded-full transition-all duration-200"
                  title="Search"
                >
                  <Search className="w-5 h-5" />
                </button> */}

                {/* Wishlist Link */}
                {/* <Link 
                  to="/wishlist" 
                  className="relative p-2 sm:p-2.5 text-[#4A0E17] hover:text-[#6B1320] hover:bg-[#F4EFE6] rounded-full transition-all duration-200"
                  title="Saved Items"
                >
                  <Heart className="w-5 h-5" />
                  {wishlistCount > 0 && (
                    <span className="absolute top-1 right-1 bg-[#4A0E17] text-[#FDFBF7] text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center shadow-sm">
                      {wishlistCount}
                    </span>
                  )}
                </Link> */}

                {/* Direct Page Navigation Cart Link */}
                {/* <Link
                  to="/cart"
                  className="relative flex items-center space-x-2 bg-[#4A0E17] hover:bg-[#6B1320] text-[#FDFBF7] px-3 sm:px-3.5 py-1.5 sm:py-2 rounded-full transition-all duration-300 shadow-sm group border border-[#6B1320]"
                  title="View Shopping Cart"
                >
                  <ShoppingBag className="w-4 h-4 text-[#FDFBF7] group-hover:scale-110 transition-transform duration-200" />
                  <span className="text-xs font-serif font-bold tracking-wide uppercase hidden sm:inline">Bag</span>
                  {cartCount > 0 && (
                    <span className="bg-[#6B1320] group-hover:bg-[#4A0E17] text-[#FDFBF7] text-[10px] font-bold w-4 h-4 sm:w-5 sm:h-5 rounded-full flex items-center justify-center transition-colors border border-[#E8DFC8]/40">
                      {cartCount}
                    </span>
                  )}
                </Link> */}
              </div>
            </>
          )}

        </div>
      </header>

      {/* Mobile Drawer Menu */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden bg-[#20050A]/60 backdrop-blur-sm">
          <div className="fixed inset-y-0 left-0 w-4/5 max-w-sm bg-[#FDFBF7] p-6 shadow-2xl flex flex-col justify-between border-r border-[#E8DFC8]">
            <div>
              <div className="flex items-center justify-between pb-6 border-b border-[#E8DFC8]">
                <span className="text-2xl font-serif font-bold text-[#4A0E17]">
                  Vedhyas<span className="text-[#6B1320]">.</span>
                </span>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-1 text-[#4A0E17] hover:text-[#6B1320]"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <nav className="mt-6 flex flex-col space-y-3">
                {navLinks.map((link) => (
                  <Link
                    key={link.name}
                    to={link.path}
                    className={`text-sm font-serif font-bold uppercase tracking-wider py-2 border-b border-[#E8DFC8]/60 flex items-center justify-between ${
                      isActive(link.path) ? 'text-[#4A0E17]' : 'text-[#7A6B5D]'
                    }`}
                  >
                    <span>{link.name}</span>
                    <ArrowRight className="w-4 h-4 opacity-40" />
                  </Link>
                ))}
              </nav>
            </div>

            <div className="pt-6 border-t border-[#E8DFC8] space-y-2 text-xs text-[#7A6B5D]">
              <p className="font-serif font-bold text-[#4A0E17]">Atelier Assistance</p>
              <p>Email: concierge@vedhyas.com</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;